/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable max-lines */

import { isDefined } from "@clipboard-health/util-ts";
import { useDiscardLatenessBooktimeThreshold } from "@src/appV2/ClipboardScore/hooks/featureFlags";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import {
  BookabilityDecision,
  type BookabilityStatusItem,
  BookabilityUnmetCriteria,
} from "@src/appV2/OpenShifts/ShiftAction";
import { getBookabilityUnmetCriteriaMetadata } from "@src/appV2/OpenShifts/ShiftAction/getBookabilityUnmetCriteriaMetadata";
import {
  hasAdditionalUnmetBookabilityCriteria,
  hasUnmetBookabilityCriteria,
} from "@src/appV2/OpenShifts/ShiftAction/hasUnmetBookabilityCriteria";
import { type ComputedArrivalData } from "@src/appV2/redesign/ArrivalTime/computeArrivalData";
import { useGetShiftBoosts } from "@src/appV2/redesign/Shift/ShiftBoost/api/useGetShiftBoosts";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { useShouldShowBlockingSkillsAssessmentBookability } from "@src/appV2/SkillsAssessment/useShouldShowBlockingSkillsAssessmentBookability";
import { useShouldShowClipboardOrientationBookability } from "@src/appV2/SkillsAssessment/useShouldShowClipboardOrientationBookability";
import { type Worker } from "@src/appV2/Worker/api/types";
import { parseISO } from "date-fns";
import type React from "react";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import { calculateMissingCredential } from "../../Documents/utils/calculateMissingCredential";
import { AnalyticsOpenFromValues } from "../../IndependantContractorBadge/constants";
import { WorkBadgeReviewPolicySource } from "../../WorkBadges/components/Policy/types";
import { type BlockingRequirementsContext } from "../Bookability/Documents/types";
import { ShiftType } from "../constants";
import { type Window } from "../Open/types";
import { BaseShiftBoostCard } from "../ShiftBoost/BaseShiftBoostCard";
import { type ModalWorkplaceData, type WindowRestriction } from "../types";
import { ShiftBottomSheetArrivalTimeCard } from "./ArrivalTime/Card";
import { BaseIncompleteChartingCard } from "./BaseIncompleteChartingCard";
import { BaseWorkBadgeRequiredCardContainer } from "./BaseWorkBadgeRequiredCardContainer";
import { ShiftBottomSheetBlockingRequirementsContainer } from "./BlockingRequirements/Container";
import { ShiftBottomSheetBookingRequestOverlapCard } from "./BookingRequestOverlapCard";
import { ShiftBottomSheetBookingRequestOverworkCard } from "./BookingRequestOverworkCard";
import { ClipboardOrientationRequiredCard } from "./ClipboardOrientationRequiredCard";
import { ShiftBottomSheetHourRestrictionsCard } from "./FacilityHourRestrictionsCard";
import { ShiftBottomSheetMarkedAsInterestedCard } from "./MarkedAsInterestedCard";
import { MissingCredentialCardContent } from "./MissingCredentialCard/MissingCredentialCardContent";
import { MissingLicenseCardContainer } from "./MissingLicenseCard/MissingLicenseCardContainer";
import { ShiftBottomSheetNotInstantBookableCard } from "./NotInstantBookableCard";
import { BaseOrientationRequiredCard } from "./OrientationRequiredCard";
import { ShiftBottomSheetPriorityShiftCard } from "./PriorityCard";
import { ShiftBottomSheetQuizCard } from "./QuizCard";
import { ShiftBottomSetupPaymentsCard } from "./SetupPaymentsCard";
import { ShiftFailedLoadingCard as BaseShiftFailedLoadingCard } from "./ShiftFailedLoadingCard";
import { ShiftLateCancelProtectionCard } from "./ShiftLateCancelProtectionCard";
import { ShiftNotBookableCard as BaseShiftNotBookableCard } from "./ShiftNotBookableCard";
import { ShiftBottomSheetShiftRequestedCard } from "./ShiftRequestedCard";
import { ShiftBottomSignAgreementCard } from "./SignAgreementCard";
import { SkillAssessmentsRequiredCard } from "./SkillAssessmentsCard/SkillAssessmentsRequiredCard";
import { Ten99PolicyShiftCoverageCard } from "./Ten99PolicyShiftCoverageCard";
import { ShiftBottomSheetUrgentShiftCard } from "./UrgentShiftCard";
import { WindowedOutCard } from "./WindowedOutCard";

export enum ShiftBottomSheetBookabilityCard {
  AGREEMENT_TO_SIGN = "agreement_to_sign",
  ARRIVAL_TIME = "arrival_time",
  BLOCKING_REQUIREMENTS = "blocking_requirements",
  BOOKING_REQUEST_OVERLAP = "booking_request_overlap",
  BOOKING_REQUEST_OVERWORK = "booking_request_overwork",
  CLIPBOARD_ORIENTATION = "clipboard_orientation",
  FAILED_LOADING = "failed_loading",
  HOURS_RESTRICTION = "hours_restriction",
  INCOMPLETE_CHARTING = "incomplete_charting",
  INDEPENDENT_CONTRACTOR_BADGE_REQUIRED = "independent_contractor_badge_required",
  LATE_CANCEL_PROTECTION = "late_cancel_protection",
  MARKED_AS_INTERESTED = "marked_as_interested",
  MISSING_LICENSE = "missing_license",
  NOT_BOOKABLE = "not_bookable",
  NOT_INSTANT_BOOKABLE = "not_instant_bookable",
  PAYMENT_DISABLED = "payment_disabled",
  PRIORITY = "priority",
  QUIZ = "quiz",
  SHIFT_BOOST = "shift_boost",
  SHIFT_REQUESTED = "shift_requested",
  SKILLS_ASSESSMENT = "skills_assessment",
  URGENT = "urgent",
  WINDOWED_OUT = "windowed_out",
  TEN99_POLICY_COVERAGE = "ten99_policy_coverage",
  ORIENTATION_REQUIRED = "orientation_required",
  MISSING_CREDENTIAL = "missing_credential",
}

/* eslint-disable react/no-unused-prop-types */
export interface CardProps {
  shiftId: string;
  shiftType: ShiftType;
  shiftQualification: string;
  workplace: ModalWorkplaceData;
  bookabilityStatus: BookabilityStatusItem;
  blockingRequirementsContext?: BlockingRequirementsContext;
  hasMarkedInterest?: boolean;
  shiftFailedLoading: boolean;
  hasHoursRestrictionConflict: boolean;
  shiftStartIso?: string;
  workplaceTimezone?: string;
  isBookable: boolean;
  canSeeBookingRequests: boolean;
  hasRequestedShift: boolean;
  cardsToSkip: ShiftBottomSheetBookabilityCard[];
  worker: Worker;
  onRenderDecision: (
    cardId: ShiftBottomSheetBookabilityCard,
    willRender: boolean,
    unmetBookabilityCriteria: BookabilityUnmetCriteria | undefined
  ) => void;
  restrictedToWindow?: WindowRestriction;
  score?: number;
  isIndependentContractorBadgesRequired?: boolean;
  priorityTill?: Date;
  window?: Window;
  isShortLeadTime?: boolean;
  computedArrivalData?: ComputedArrivalData;
  hasTen99PolicyCoverage?: boolean;
}
/* eslint-enable react/no-unused-prop-types */

interface BookabilityCardProps extends CardProps {
  cardId: ShiftBottomSheetBookabilityCard;
  unmetBookabilityCriteria: BookabilityUnmetCriteria | undefined;
  shouldRender: boolean;
  children: React.ReactElement;
}

export function BookabilityCard({
  cardId,
  unmetBookabilityCriteria,
  shouldRender,
  cardsToSkip,
  onRenderDecision,
  children,
}: BookabilityCardProps) {
  useEffect(() => {
    onRenderDecision?.(cardId, shouldRender, unmetBookabilityCriteria);
  }, [shouldRender, onRenderDecision, cardId, unmetBookabilityCriteria]);

  if (!shouldRender || cardsToSkip.includes(cardId)) {
    return null;
  }

  return children;
}

// Card components
export function QuizCard(props: CardProps) {
  const { bookabilityStatus, shiftId } = props;
  const shouldRender = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_QUIZ_FOR_WORKPLACE_RULES,
  ]);

  const workerQuizResultStatus =
    bookabilityStatus.attributes.bookability.decision === BookabilityDecision.BLOCKED
      ? bookabilityStatus.attributes.bookability.unmetCriteriaWithMetadata.find(
          (unmetCriteriaWithMetadata) =>
            unmetCriteriaWithMetadata.unmetCriteria ===
            BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_QUIZ_FOR_WORKPLACE_RULES
        )?.metadata?.workerQuizResultStatus
      : undefined;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.QUIZ}
      unmetBookabilityCriteria={
        BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_QUIZ_FOR_WORKPLACE_RULES
      }
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSheetQuizCard shiftId={shiftId} quizResultStatus={workerQuizResultStatus} />
    </BookabilityCard>
  );
}

export function MissingLicenseCard(props: CardProps) {
  const shouldRender = hasUnmetBookabilityCriteria(props.bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_MISSING_LICENSE,
  ]);

  const metadata = getBookabilityUnmetCriteriaMetadata(
    props.bookabilityStatus,
    BookabilityUnmetCriteria.WORKER_MISSING_LICENSE
  );

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.MISSING_LICENSE}
      unmetBookabilityCriteria={BookabilityUnmetCriteria.WORKER_MISSING_LICENSE}
      shouldRender={shouldRender}
      {...props}
    >
      <MissingLicenseCardContainer
        metadata={metadata ?? { status: "missing", licenseIds: [] }}
        shiftQualification={props.shiftQualification}
        workplaceState={props.workplace.attributes?.fullAddress?.state}
      />
    </BookabilityCard>
  );
}

export function SkillAssessmentCard(props: CardProps) {
  const shouldRender = useShouldShowBlockingSkillsAssessmentBookability(props.bookabilityStatus);

  const metadata = getBookabilityUnmetCriteriaMetadata(
    props.bookabilityStatus,
    BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_ASSESSMENTS
  );

  if (!isDefined(metadata)) {
    return null;
  }

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.SKILLS_ASSESSMENT}
      unmetBookabilityCriteria={BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_ASSESSMENTS}
      shouldRender={shouldRender}
      {...props}
    >
      <SkillAssessmentsRequiredCard metadata={metadata} />
    </BookabilityCard>
  );
}

export function ClipboardOrientationCard(props: CardProps) {
  const shouldRender = useShouldShowClipboardOrientationBookability(props.bookabilityStatus);

  const metadata = getBookabilityUnmetCriteriaMetadata(
    props.bookabilityStatus,
    BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_ASSESSMENTS
  );

  if (!isDefined(metadata)) {
    return null;
  }

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.CLIPBOARD_ORIENTATION}
      unmetBookabilityCriteria={BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_ASSESSMENTS}
      shouldRender={shouldRender}
      {...props}
    >
      <ClipboardOrientationRequiredCard metadata={metadata} />
    </BookabilityCard>
  );
}

export function HoursRestrictionCard(props: CardProps) {
  const hasHourRestrictions =
    isDefined(props.workplace.attributes?.maxAllowedWorkConsecutiveHours) ||
    isDefined(props.workplace.attributes?.maxAllowedWorkHoursPerWeek);

  const shouldRender = hasHourRestrictions && props.hasHoursRestrictionConflict;

  const { maxAllowedWorkConsecutiveHours, maxAllowedWorkHoursPerWeek, preventDoubleShifts } =
    props.workplace.attributes ?? {};

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.HOURS_RESTRICTION}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSheetHourRestrictionsCard
        maxAllowedWorkConsecutiveHours={maxAllowedWorkConsecutiveHours}
        maxAllowedWorkHoursPerWeek={maxAllowedWorkHoursPerWeek}
        preventDoubleShifts={preventDoubleShifts}
      />
    </BookabilityCard>
  );
}

export function SignAgreementCard(props: CardProps) {
  const shouldRender = hasUnmetBookabilityCriteria(props.bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_UNSIGNED_AGREEMENT,
  ]);

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.AGREEMENT_TO_SIGN}
      unmetBookabilityCriteria={BookabilityUnmetCriteria.WORKER_UNSIGNED_AGREEMENT}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSignAgreementCard shiftId={props.shiftId} />
    </BookabilityCard>
  );
}

export function PaymentDisabledCard(props: CardProps) {
  const shouldRender = hasUnmetBookabilityCriteria(props.bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_PAYMENTS_DISABLED,
  ]);

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.PAYMENT_DISABLED}
      unmetBookabilityCriteria={BookabilityUnmetCriteria.WORKER_PAYMENTS_DISABLED}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSetupPaymentsCard shiftId={props.shiftId} />
    </BookabilityCard>
  );
}

export function BlockingRequirementsCard(props: CardProps) {
  const shouldRender =
    isDefined(props.blockingRequirementsContext) &&
    props.blockingRequirementsContext?.hasBlockingRequirements;

  const hasAgreementToSign = hasUnmetBookabilityCriteria(props.bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_UNSIGNED_AGREEMENT,
  ]);

  const hasPaymentsDisabled = hasUnmetBookabilityCriteria(props.bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_PAYMENTS_DISABLED,
  ]);

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.BLOCKING_REQUIREMENTS}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSheetBlockingRequirementsContainer
        shiftId={props.shiftId}
        blockingRequirementsContext={props.blockingRequirementsContext}
        hasExternalBlockingCards={hasAgreementToSign || hasPaymentsDisabled}
        workplaceId={props.workplace.id}
      />
    </BookabilityCard>
  );
}

export function UrgentShiftCard(props: CardProps) {
  const { discardLatenessBooktimeThresholdInHours } = useDiscardLatenessBooktimeThreshold();

  if (
    !isDefined(props.shiftStartIso) ||
    // we don't want to show the urgent shift card if the shift is short lead time
    (props.isShortLeadTime && isDefined(props.computedArrivalData?.targetArrivalTime))
  ) {
    return null;
  }

  const shouldRender = props.shiftType === ShiftType.URGENT && isDefined(props.shiftStartIso);

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.URGENT}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSheetUrgentShiftCard
        startDate={parseISO(props.shiftStartIso)}
        discardLatenessBooktimeThresholdInHours={discardLatenessBooktimeThresholdInHours}
      />
    </BookabilityCard>
  );
}

export function PriorityShiftCard(props: CardProps) {
  const shouldRender = props.shiftType === ShiftType.PRIORITY;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.PRIORITY}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSheetPriorityShiftCard workplaceId={props.workplace.id} window={props.window} />
    </BookabilityCard>
  );
}

export function NotInstantBookableCard(props: CardProps) {
  const shouldRender =
    hasUnmetBookabilityCriteria(props.bookabilityStatus, [
      BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED,
    ]) &&
    props.canSeeBookingRequests &&
    !props.hasMarkedInterest &&
    !props.hasRequestedShift;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.NOT_INSTANT_BOOKABLE}
      unmetBookabilityCriteria={BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSheetNotInstantBookableCard
        areBookingRequestsEnabled={props.canSeeBookingRequests}
      />
    </BookabilityCard>
  );
}

export function ShiftFailedLoadingCard(props: CardProps) {
  const shouldRender = props.isBookable && props.shiftFailedLoading;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.FAILED_LOADING}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <BaseShiftFailedLoadingCard />
    </BookabilityCard>
  );
}

export function ShiftNotBookableCard(props: CardProps) {
  const shouldRender = !props.isBookable;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.NOT_BOOKABLE}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <BaseShiftNotBookableCard />
    </BookabilityCard>
  );
}

export function LateCancelProtectionCard(props: CardProps) {
  const isUnifyLateCancellationPolicyAndPayoutEnabled = useCbhFlag(
    CbhFeatureFlag.UNIFY_LATE_CANCELLATION_POLICY_AND_PAYOUT,
    { defaultValue: false }
  );

  const shouldRender = props.isBookable && isUnifyLateCancellationPolicyAndPayoutEnabled;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.LATE_CANCEL_PROTECTION}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftLateCancelProtectionCard />
    </BookabilityCard>
  );
}

export function Ten99PolicyCoverageCard(props: CardProps) {
  const shouldRender = props.hasTen99PolicyCoverage ?? false;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.TEN99_POLICY_COVERAGE}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <Ten99PolicyShiftCoverageCard />
    </BookabilityCard>
  );
}

export function ShiftBoostCard(props: CardProps) {
  const shiftBoostConfig = useCbhFlag(CbhFeatureFlag.SHIFT_BOOST_CONFIG, {
    defaultValue: { enabled: false },
  });

  const { userId: workerId } = props.worker;

  const { data: shiftBoosts, isSuccess: getShiftBoostsSuccess } = useGetShiftBoosts(
    { workerId, statuses: ["AVAILABLE"] },
    { enabled: shiftBoostConfig.enabled }
  );

  const shouldRender =
    props.isBookable &&
    shiftBoostConfig.enabled &&
    getShiftBoostsSuccess &&
    shiftBoosts.data.length > 0;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.SHIFT_BOOST}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <BaseShiftBoostCard shiftBoosts={shiftBoosts?.data ?? []} />
    </BookabilityCard>
  );
}

export function BookingRequestOverlapCard(props: CardProps) {
  const shouldRender =
    hasUnmetBookabilityCriteria(props.bookabilityStatus, [
      BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED,
    ]) &&
    hasUnmetBookabilityCriteria(props.bookabilityStatus, [
      BookabilityUnmetCriteria.SHIFT_OVERLAPPING,
    ]) &&
    props.canSeeBookingRequests &&
    !props.hasMarkedInterest &&
    !props.hasRequestedShift;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.BOOKING_REQUEST_OVERLAP}
      unmetBookabilityCriteria={BookabilityUnmetCriteria.SHIFT_OVERLAPPING}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSheetBookingRequestOverlapCard />
    </BookabilityCard>
  );
}

export function BookingRequestOverworkCard(props: CardProps) {
  const shouldRender =
    hasUnmetBookabilityCriteria(props.bookabilityStatus, [
      BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED,
    ]) &&
    hasUnmetBookabilityCriteria(props.bookabilityStatus, [
      BookabilityUnmetCriteria.SHIFT_OVERWORKING,
    ]) &&
    props.canSeeBookingRequests &&
    !props.hasMarkedInterest &&
    !props.hasRequestedShift;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.BOOKING_REQUEST_OVERWORK}
      unmetBookabilityCriteria={BookabilityUnmetCriteria.SHIFT_OVERWORKING}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSheetBookingRequestOverworkCard />
    </BookabilityCard>
  );
}

export function ShiftRequestedCard(props: CardProps) {
  const shouldRender = props.canSeeBookingRequests && props.hasRequestedShift;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.SHIFT_REQUESTED}
      unmetBookabilityCriteria={BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSheetShiftRequestedCard />
    </BookabilityCard>
  );
}

export function MarkedAsInterestedCard(props: CardProps) {
  const shouldRender =
    hasUnmetBookabilityCriteria(props.bookabilityStatus, [
      BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED,
    ]) &&
    isDefined(props.hasMarkedInterest) &&
    props.hasMarkedInterest;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.MARKED_AS_INTERESTED}
      unmetBookabilityCriteria={BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSheetMarkedAsInterestedCard />
    </BookabilityCard>
  );
}

export function WindowedOutCardWrapper(props: CardProps) {
  const windowedOutShiftsEnabled = useCbhFlag(CbhFeatureFlag.INCLUDE_WINDOWED_OUT_SHIFTS, {
    defaultValue: false,
  });

  // Only show windowing card if there are no other hard blocking criteria.
  // If the worker has blocking issues (missing license, payment problems, etc.),
  // those cards should be shown instead of the windowing message.
  const shouldRender =
    isDefined(props.restrictedToWindow) && windowedOutShiftsEnabled && props.isBookable;

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.WINDOWED_OUT}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <WindowedOutCard
        windowName={props.restrictedToWindow?.windowName ?? ""}
        windowScoreThreshold={props.restrictedToWindow?.windowScoreThreshold}
        score={props.score}
        shiftStartIso={props.shiftStartIso}
        shiftId={props.shiftId}
        workplaceId={props.workplace.id}
      />
    </BookabilityCard>
  );
}

export function IncompleteChartingCard(props: CardProps) {
  const shouldRender = hasUnmetBookabilityCriteria(props.bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_HAS_INCOMPLETE_CHARTING,
  ]);
  const history = useHistory();

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.INCOMPLETE_CHARTING}
      unmetBookabilityCriteria={BookabilityUnmetCriteria.WORKER_HAS_INCOMPLETE_CHARTING}
      shouldRender={shouldRender}
      {...props}
    >
      <BaseIncompleteChartingCard history={history} />
    </BookabilityCard>
  );
}

export function IndependentContractorBadgeRequiredCard(props: CardProps) {
  const shouldRender = props.isIndependentContractorBadgesRequired === true && props.isBookable;

  const analyticsMetadata = {
    shiftId: props.shiftId,
    state: props.workplace.attributes?.fullAddress?.state ?? "",
    qualification: props.shiftQualification,
    workplaceId: props.workplace.id,
    workplaceName: props.workplace.attributes.name,
    workerId: props.worker.userId,
    workerFullName: `${props.worker.firstName} ${props.worker.lastName}`,
    role: props.shiftQualification,
    openedFrom: AnalyticsOpenFromValues.FIND_SHIFT_DETAILS,
    hasShiftWithBadgeRequired: props.isIndependentContractorBadgesRequired === true,
  };

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.INDEPENDENT_CONTRACTOR_BADGE_REQUIRED}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <BaseWorkBadgeRequiredCardContainer
        analyticsMetadata={analyticsMetadata}
        shiftId={props.shiftId}
        state={props.workplace.attributes?.fullAddress?.state ?? ""}
        qualification={props.shiftQualification}
        source={WorkBadgeReviewPolicySource.SHIFT_DISCOVERY}
      />
    </BookabilityCard>
  );
}

export function ArrivalTimeCard(props: CardProps) {
  const { computedArrivalData, workplaceTimezone, isShortLeadTime } = props;

  if (!isDefined(computedArrivalData)) {
    return null;
  }

  const shouldRender = Boolean(isShortLeadTime);

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.ARRIVAL_TIME}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <ShiftBottomSheetArrivalTimeCard
        // these default values here don't matter that much as we don't render the card if the conditions are not met
        computedArrivalData={computedArrivalData}
        workplaceTimezone={workplaceTimezone ?? DEFAULT_TIMEZONE}
      />
    </BookabilityCard>
  );
}

export function OrientationRequiredCard(props: CardProps) {
  const { bookabilityStatus, shiftId, worker, workplace, shiftQualification } = props;

  const shouldRender = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_HAS_NOT_COMPLETED_ORIENTATION,
  ]);

  const shiftHasAdditionalUnmetBookabilityCriteria = hasAdditionalUnmetBookabilityCriteria(
    bookabilityStatus,
    [BookabilityUnmetCriteria.WORKER_HAS_NOT_COMPLETED_ORIENTATION]
  );

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.ORIENTATION_REQUIRED}
      unmetBookabilityCriteria={BookabilityUnmetCriteria.WORKER_HAS_NOT_COMPLETED_ORIENTATION}
      shouldRender={shouldRender}
      {...props}
    >
      <BaseOrientationRequiredCard
        shiftId={shiftId}
        worker={worker}
        workplace={workplace}
        shiftQualification={shiftQualification}
        shiftHasAdditionalUnmetBookabilityCriteria={shiftHasAdditionalUnmetBookabilityCriteria}
      />
    </BookabilityCard>
  );
}

export function MissingCredentialCard(props: CardProps) {
  const { shiftQualification, workplace, blockingRequirementsContext } = props;
  const { baseCredentialRequirements = [], workplaceCredentialRequirements = [] } =
    blockingRequirementsContext ?? {};

  const missingCredentialType = calculateMissingCredential([
    ...baseCredentialRequirements,
    ...workplaceCredentialRequirements,
  ]);

  // we only expect one credential requirement per type, having more than one would be an error in configuration
  const missingCredentialRequirement =
    missingCredentialType === "BASE_CREDENTIAL"
      ? baseCredentialRequirements[0]
      : workplaceCredentialRequirements[0];

  const workplaceState = workplace.attributes?.fullAddress?.state;
  const shouldRender = isDefined(missingCredentialRequirement) && isDefined(missingCredentialType);

  return (
    <BookabilityCard
      cardId={ShiftBottomSheetBookabilityCard.MISSING_CREDENTIAL}
      unmetBookabilityCriteria={undefined}
      shouldRender={shouldRender}
      {...props}
    >
      <MissingCredentialCardContent
        missingCredentialRequirement={missingCredentialRequirement}
        missingCredentialType={missingCredentialType!}
        shiftQualification={shiftQualification}
        workplaceState={workplaceState}
      />
    </BookabilityCard>
  );
}

export const CARDS: Array<React.ComponentType<CardProps>> = [
  ShiftNotBookableCard,
  MissingLicenseCard,
  MissingCredentialCard,
  BlockingRequirementsCard,
  SignAgreementCard,
  WindowedOutCardWrapper,
  SkillAssessmentCard,
  ClipboardOrientationCard,
  PaymentDisabledCard,
  ArrivalTimeCard,
  IncompleteChartingCard,
  ShiftFailedLoadingCard,
  QuizCard,
  IndependentContractorBadgeRequiredCard,
  HoursRestrictionCard,
  ShiftRequestedCard,
  MarkedAsInterestedCard,
  BookingRequestOverlapCard,
  BookingRequestOverworkCard,
  NotInstantBookableCard,
  ShiftBoostCard,
  UrgentShiftCard,
  PriorityShiftCard,
  LateCancelProtectionCard,
  Ten99PolicyCoverageCard,
  OrientationRequiredCard,
];
